import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { sanitizeStrict } from '../../lib/sanitize';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Progress } from '../../components/ui/progress';
import { Badge } from '../../components/ui/badge';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { api } from '../../lib/api';
import { toast } from 'sonner';
import { Plus, Sparkles, Trash2, Building2, AlertTriangle, Loader2, FileText, Wallet, Pencil, Send, CheckCircle2, Receipt, X, Save, RefreshCw } from 'lucide-react';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../../components/ui/alert-dialog';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { RiepilogoCantiereDialog } from '../../components/AIPanels';

const STATO_COLORS = {
    non_iniziato: 'bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))]',
    in_corso: 'bg-[hsl(210_90%_94%)] text-[hsl(210_90%_40%)]',
    completato: 'bg-[hsl(145_45%_94%)] text-[hsl(145_55%_34%)]',
    sospeso: 'bg-[hsl(0_80%_95%)] text-[hsl(0_72%_52%)]',
};

export default function SAL() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const location = useLocation();
    const [cantieri, setCantieri] = useState([]);
    const [selected, setSelected] = useState(null);
    const [isRefreshing, setIsRefreshing] = useState(false);
    // Keep latest selected.id available inside event handlers without re-binding effects.
    const selectedIdRef = useRef(null);
    useEffect(() => { selectedIdRef.current = selected?.id || null; }, [selected]);
    const [openNew, setOpenNew] = useState(false);
    const [analyzing, setAnalyzing] = useState(false);
    const [analysis, setAnalysis] = useState(null);
    const [showRiepilogo, setShowRiepilogo] = useState(false);
    const [newCantiere, setNewCantiere] = useState({ nome: '', cliente: '', indirizzo: '', importo: 0 });
    // Tranche state
    const [tranche, setTranche] = useState([]);
    const [editTranche, setEditTranche] = useState(false);
    const [trancheDraft, setTrancheDraft] = useState([]);
    const [savingTranche, setSavingTranche] = useState(false);
    const [emettendoFattura, setEmettendoFattura] = useState(false);
    const [confermaPagDialog, setConfermaPagDialog] = useState(null); // tranche obj
    const [pagMetodo, setPagMetodo] = useState('bonifico');
    const [pagRiferimento, setPagRiferimento] = useState('');
    const [pagLoading, setPagLoading] = useState(false);
    const [fatturaResult, setFatturaResult] = useState(null);

    const STATO_LABELS = {
        non_iniziato: t('pages.sal_status_non_iniziato'),
        in_corso: t('pages.sal_status_in_corso'),
        completato: t('pages.sal_status_completato'),
        sospeso: t('pages.sal_status_sospeso'),
    };

    const load = useCallback(async () => {
        const res = await api.get('/cantieri/list');
        setCantieri(res.data);
        // Deep-link: ?cantiere=ID opens that specific cantiere (used from Fatture → Proforme SAL)
        const deepId = searchParams.get('cantiere');
        if (deepId && res.data.some((c) => c.id === deepId)) {
            const detailed = await api.get(`/cantieri/${deepId}`);
            setSelected(detailed.data);
            try {
                const r = await api.get(`/cantieri/${deepId}/tranche`);
                setTranche(r.data.tranche || []);
            } catch (e) { /* ignore */ }
            return;
        }
        // No deep-link: if we already had something selected (returning from another page),
        // re-fetch its tranche/details so the SAL payments list stays in sync.
        const keepId = selectedIdRef.current;
        if (keepId && res.data.some((c) => c.id === keepId)) {
            try {
                const detailed = await api.get(`/cantieri/${keepId}`);
                setSelected(detailed.data);
                const r = await api.get(`/cantieri/${keepId}/tranche`);
                setTranche(r.data.tranche || []);
            } catch (e) { /* ignore */ }
            return;
        }
        if (res.data.length > 0) {
            const detailed = await api.get(`/cantieri/${res.data[0].id}`);
            setSelected(detailed.data);
            try {
                const r = await api.get(`/cantieri/${res.data[0].id}/tranche`);
                setTranche(r.data.tranche || []);
            } catch (e) { /* ignore */ }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchParams]);

    // Initial load + reload on route change (e.g. returning to /app/sal from another page)
    useEffect(() => {
        load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.key]);

    // Auto-refresh when the tab becomes visible again or window regains focus.
    // This fixes the issue where leaving SAL, doing other things and coming back
    // shows stale data (proforme SAL didn't appear until the user clicked something).
    useEffect(() => {
        const onVisible = () => {
            if (document.visibilityState === 'visible') {
                load();
            }
        };
        const onFocus = () => load();
        document.addEventListener('visibilitychange', onVisible);
        window.addEventListener('focus', onFocus);
        return () => {
            document.removeEventListener('visibilitychange', onVisible);
            window.removeEventListener('focus', onFocus);
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Manual refresh helper (used by the refresh button)
    const refreshAll = async () => {
        setIsRefreshing(true);
        try { await load(); }
        finally { setIsRefreshing(false); }
    };

    const loadTranche = async (cantiereId, alreadyHas) => {
        try {
            if (!alreadyHas) {
                const init = await api.post(`/cantieri/${cantiereId}/tranche/init`);
                setTranche(init.data.tranche || []);
                return;
            }
            const r = await api.get(`/cantieri/${cantiereId}/tranche`);
            setTranche(r.data.tranche || []);
        } catch (e) {
            console.error('[SAL] loadTranche error:', e);
            setTranche([]);
        }
    };

    const selectCantiere = async (id) => {
        const res = await api.get(`/cantieri/${id}`);
        setSelected(res.data);
        setAnalysis(res.data.ultima_analisi || null);
        // Load tranche (auto-init if empty)
        await loadTranche(id, !!res.data.tranche?.length);
    };

    const manualInitTranche = async () => {
        if (!selected) return;
        try {
            const r = await api.post(`/cantieri/${selected.id}/tranche/init`);
            setTranche(r.data.tranche || []);
            toast.success(t('pages.sal_tranche_init_ok') || 'Tranche inizializzate');
        } catch (e) {
            console.error('[SAL] manualInitTranche error:', e);
            toast.error(e.response?.data?.detail || (t('common.generic_error') || 'Errore'));
        }
    };

    const startEditTranche = () => {
        setTrancheDraft(tranche.map((t) => ({ ...t })));
        setEditTranche(true);
    };
    const cancelEditTranche = () => {
        setTrancheDraft([]);
        setEditTranche(false);
    };
    const addTrancheRow = () => setTrancheDraft([...trancheDraft, { nome: `SAL ${trancheDraft.length}`, soglia_avanzamento_pct: 50, importo_pct: 10, fatturabile: false }]);
    const delTrancheRow = (i) => setTrancheDraft(trancheDraft.filter((_, idx) => idx !== i));
    const updTrancheRow = (i, patch) => setTrancheDraft(trancheDraft.map((t, idx) => idx === i ? { ...t, ...patch } : t));
    const sumPct = (arr) => arr.reduce((s, t) => s + (parseFloat(t.importo_pct) || 0), 0);

    const saveTrancheConfig = async () => {
        if (Math.abs(sumPct(trancheDraft) - 100) > 0.5) {
            toast.error(t('pages.sal_tranche_sum_err', { pct: sumPct(trancheDraft).toFixed(1) }) || `Le percentuali devono sommare 100% (attuale: ${sumPct(trancheDraft).toFixed(1)}%)`);
            return;
        }
        setSavingTranche(true);
        try {
            const payload = {
                tranche: trancheDraft.map((t) => ({
                    nome: t.nome,
                    soglia_avanzamento_pct: parseFloat(t.soglia_avanzamento_pct) || 0,
                    importo_pct: parseFloat(t.importo_pct) || 0,
                    fatturabile: !!t.fatturabile,
                })),
            };
            const r = await api.put(`/cantieri/${selected.id}/tranche`, payload);
            setTranche(r.data.tranche);
            setEditTranche(false);
            setTrancheDraft([]);
            toast.success(t('pages.sal_tranche_saved') || 'Configurazione salvata');
        } catch (e) {
            toast.error(e.response?.data?.detail || (t('common.generic_error') || 'Errore'));
        } finally {
            setSavingTranche(false);
        }
    };

    const [inviaDialog, setInviaDialog] = useState(null); // tranche being sent
    const [inviaEmail, setInviaEmail] = useState('');
    const [inviaSubject, setInviaSubject] = useState('');
    const [inviaMessage, setInviaMessage] = useState('');
    const [inviaLoading, setInviaLoading] = useState(false);

    const openInviaDialog = (tr) => {
        // Prefill recipient: cantiere.cliente_email > preventivo.cliente_email
        const recip = selected?.cliente_email || selected?.preventivo_cliente_email || '';
        setInviaEmail(recip);
        const companyName = selected?.nome || '';
        const numero = tr?.proforma_numero || '';
        setInviaSubject(`Proforma ${numero ? numero + ' · ' : ''}${tr?.nome || 'SAL'}${companyName ? ' · ' + companyName : ''}`);
        setInviaMessage('');
        setInviaDialog(tr);
    };

    const inviaProforma = async () => {
        if (!inviaDialog) return;
        const email = (inviaEmail || '').trim();
        if (!email || !email.includes('@')) {
            toast.error(t('pages.sal_invia_email_required') || 'Inserisci una email destinatario valida');
            return;
        }
        setInviaLoading(true);
        try {
            const r = await api.post(`/cantieri/${selected.id}/tranche/${inviaDialog.id}/invia`, {
                cliente_email: email,
                subject: inviaSubject || undefined,
                message: inviaMessage || undefined,
            });
            setTranche(r.data.tranche);
            // Persist cliente_email on the local cantiere object for subsequent sends
            setSelected((s) => s ? { ...s, cliente_email: email } : s);
            if (r.data.email_sent) {
                toast.success(t('pages.sal_tranche_inviata') || `Proforma inviata a ${email}`);
            } else if (r.data.warning) {
                toast.warning(r.data.warning);
            }
            setInviaDialog(null);
        } catch (e) {
            toast.error(e.response?.data?.detail || (t('common.generic_error') || 'Errore'));
        } finally {
            setInviaLoading(false);
        }
    };

    const confermaPagamentoTranche = async () => {
        if (!confermaPagDialog?.proforma_id) return;
        setPagLoading(true);
        try {
            await api.post(`/proforme/${confermaPagDialog.proforma_id}/conferma-pagamento`, {
                metodo: pagMetodo,
                riferimento: pagRiferimento,
            });
            toast.success(t('pages.sal_tranche_pagata') || 'Pagamento confermato');
            setConfermaPagDialog(null);
            setPagRiferimento('');
            await selectCantiere(selected.id);
        } catch (e) {
            toast.error(e.response?.data?.detail || (t('common.generic_error') || 'Errore'));
        } finally {
            setPagLoading(false);
        }
    };

    const emettiFatturaFinale = async () => {
        if (!selected) return;
        setEmettendoFattura(true);
        try {
            const r = await api.post(`/cantieri/${selected.id}/emetti-fattura-finale`);
            setFatturaResult(r.data);
            await selectCantiere(selected.id);
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || (t('common.generic_error') || 'Errore'));
        } finally {
            setEmettendoFattura(false);
        }
    };

    const createCantiere = async () => {
        if (!newCantiere.nome || !newCantiere.cliente) { toast.error(t('pages.sal_toast_fill')); return; }
        const res = await api.post('/cantieri/create', newCantiere);
        toast.success(t('pages.sal_toast_created'));
        setOpenNew(false);
        setNewCantiere({ nome: '', cliente: '', indirizzo: '', importo: 0 });
        await load();
        selectCantiere(res.data.id);
    };

    const updatePhase = async (faseId, updates) => {
        const r = await api.put(`/cantieri/${selected.id}/fase/${faseId}`, updates);
        // If a tranche was triggered, notify and reload
        if (r.data.triggered_tranche && r.data.triggered_tranche.length > 0) {
            toast.success(t('pages.sal_tranche_triggered') || 'Soglia SAL raggiunta! Proforma creata in bozza.', { duration: 5000 });
        }
        await selectCantiere(selected.id);
        await load();
    };

    const removeCantiere = async (id) => {
        if (!window.confirm(t('pages.sal_confirm_delete'))) return;
        await api.delete(`/cantieri/${id}`);
        toast.success(t('pages.sal_toast_deleted'));
        setSelected(null);
        load();
    };

    const analyze = async () => {
        setAnalyzing(true);
        try {
            const res = await api.post(`/cantieri/${selected.id}/analyze`);
            setAnalysis(res.data);
            toast.success(t('pages.sal_toast_analyzed'));
        } catch (e) {
            toast.error(t('pages.sal_toast_analyze_err'));
        } finally { setAnalyzing(false); }
    };

    return (
        <div data-testid="sal-page">
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h1 className="font-display text-4xl md:text-5xl">{t('pages.sal_title')}</h1>
                    <p className="text-sm text-[hsl(var(--muted-foreground))]">{t('pages.sal_subtitle')}</p>
                </div>
                <div className="flex items-center gap-2">
                    <Button onClick={refreshAll} disabled={isRefreshing} variant="outline" size="sm" className="gap-2 h-11" data-testid="sal-refresh-button" title={t('common.refresh') || 'Aggiorna'}>
                        <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
                        <span className="hidden sm:inline">{t('common.refresh') || 'Aggiorna'}</span>
                    </Button>
                    <Button onClick={() => setOpenNew(true)} className="bg-[hsl(var(--accent))] hover:bg-[hsl(45_100%_48%)] text-[hsl(220_13%_10%)] font-bold gap-2 h-11" data-testid="sal-new-cantiere"><Plus className="w-4 h-4" /> {t('pages.sal_new')}</Button>
                </div>
            </div>

            <div className="grid lg:grid-cols-[280px_1fr] gap-5">
                <Card className="p-3">
                    <div className="text-xs tracking-widest text-[hsl(var(--muted-foreground))] mb-2 px-1">{t('pages.sal_cantieri_list')} ({cantieri.length})</div>
                    <div className="space-y-1">
                        {cantieri.map((c) => (
                            <button key={c.id} onClick={() => selectCantiere(c.id)} className={`w-full text-left p-3 rounded-lg transition-colors ${selected?.id === c.id ? 'bg-[hsl(var(--accent))]/10 border-l-4 border-[hsl(var(--accent))]' : 'hover:bg-[hsl(var(--muted))]'}`} data-testid={`sal-cantiere-${c.id}`}>
                                <div className="font-semibold text-sm truncate">{c.nome}</div>
                                <div className="text-xs text-[hsl(var(--muted-foreground))] truncate">{c.cliente}</div>
                                <div className="flex items-center gap-2 mt-1">
                                    <Progress value={c.avanzamento || 0} className="h-1.5 flex-1" />
                                    <span className="text-xs tabular-nums">{c.avanzamento || 0}%</span>
                                </div>
                            </button>
                        ))}
                        {cantieri.length === 0 && <div className="text-sm text-[hsl(var(--muted-foreground))] p-3">{t('pages.sal_no_cantiere')}</div>}
                    </div>
                </Card>

                {selected ? (
                    <div className="space-y-5">
                        <Card className="p-5">
                            <div className="flex items-start justify-between mb-4">
                                <div>
                                    <h2 className="font-display text-3xl">{selected.nome}</h2>
                                    <div className="text-sm text-[hsl(var(--muted-foreground))]">{selected.cliente} · €{selected.importo}</div>
                                </div>
                                <Button variant="ghost" onClick={() => removeCantiere(selected.id)} className="text-[hsl(var(--destructive))]"><Trash2 className="w-4 h-4" /></Button>
                            </div>
                            <div className="grid grid-cols-3 gap-3 text-center">
                                <div className="p-3 rounded-lg bg-[hsl(var(--muted))]"><div className="font-display text-2xl tabular-nums">{selected.avanzamento || 0}%</div><div className="text-xs text-[hsl(var(--muted-foreground))]">{t('pages.sal_kpi_avanz')}</div></div>
                                <div className="p-3 rounded-lg bg-[hsl(var(--muted))]"><div className="font-display text-2xl tabular-nums">€{Math.round((selected.importo || 0) * (selected.avanzamento || 0) / 100)}</div><div className="text-xs text-[hsl(var(--muted-foreground))]">{t('pages.sal_kpi_matured')}</div></div>
                                <div className="p-3 rounded-lg bg-[hsl(var(--muted))]"><div className="font-display text-2xl tabular-nums">{selected.fasi?.filter(f => f.stato === 'completato').length || 0}/{selected.fasi?.length || 0}</div><div className="text-xs text-[hsl(var(--muted-foreground))]">{t('pages.sal_kpi_phases')}</div></div>
                            </div>
                        </Card>

                        <Card className="p-5">
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="font-display text-xl">{t('pages.sal_phases_title')}</h3>
                                <div className="flex gap-2">
                                    <Button onClick={() => setShowRiepilogo(true)} variant="outline" size="sm" className="gap-2" data-testid="sal-riepilogo-button">
                                        <FileText className="w-4 h-4 text-[hsl(var(--accent))]" /> {t('pages.sal_recap_ai')}
                                    </Button>
                                    <Button onClick={analyze} disabled={analyzing} variant="outline" size="sm" className="gap-2 border-[hsl(var(--accent))] text-[hsl(220_13%_10%)] hover:bg-[hsl(var(--accent))]/10" data-testid="sal-analyze-button">
                                        {analyzing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4 text-[hsl(var(--accent))]" />}
                                        {analyzing ? t('common.analyzing') : t('pages.sal_analyze_btn')}
                                    </Button>
                                </div>
                            </div>
                            <div className="space-y-3">
                                {(selected.fasi || []).map((f) => (
                                    <div key={f.id} className="p-3 rounded-lg border border-[hsl(var(--border))]" data-testid={`sal-phase-${f.id}`}>
                                        <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                                            <div className="font-semibold">{f.nome}</div>
                                            <Select value={f.stato} onValueChange={(v) => updatePhase(f.id, { stato: v })}>
                                                <SelectTrigger className="w-44 h-8 text-xs"><SelectValue /></SelectTrigger>
                                                <SelectContent>
                                                    {Object.entries(STATO_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                                                </SelectContent>
                                            </Select>
                                        </div>
                                        <div className="flex items-center gap-3 mb-2">
                                            <input type="range" min="0" max="100" value={f.percentuale} onChange={(e) => updatePhase(f.id, { percentuale: parseInt(e.target.value) })} className="flex-1 accent-[hsl(var(--accent))]" />
                                            <Input type="number" min="0" max="100" value={f.percentuale} onChange={(e) => updatePhase(f.id, { percentuale: parseInt(e.target.value) || 0 })} className="w-20 h-8 text-sm" />
                                            <Badge className={STATO_COLORS[f.stato]}>{STATO_LABELS[f.stato]}</Badge>
                                        </div>
                                        <Input placeholder={t('pages.sal_note_ph')} defaultValue={f.note} onBlur={(e) => updatePhase(f.id, { note: e.target.value })} className="text-sm h-8" />
                                    </div>
                                ))}
                            </div>
                        </Card>

                        {/* PAGAMENTI SAL */}
                        <Card className="p-5" data-testid="sal-payments-card">
                            <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                                <h3 className="font-display text-xl flex items-center gap-2">
                                    <Wallet className="w-5 h-5 text-[hsl(var(--accent))]" /> {t('pages.sal_pagamenti_title') || 'Pagamenti SAL'}
                                </h3>
                                <div className="flex gap-2">
                                    {!editTranche ? (
                                        <Button onClick={startEditTranche} size="sm" variant="outline" className="gap-2" data-testid="sal-edit-tranche-button">
                                            <Pencil className="w-4 h-4" /> {t('pages.sal_tranche_edit') || 'Modifica %'}
                                        </Button>
                                    ) : (
                                        <>
                                            <Button onClick={saveTrancheConfig} disabled={savingTranche} size="sm" className="gap-2 bg-[hsl(var(--accent))] text-[hsl(220_13%_10%)]" data-testid="sal-save-tranche">
                                                {savingTranche ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} {t('pages.sal_tranche_save') || 'Salva'}
                                            </Button>
                                            <Button onClick={cancelEditTranche} variant="outline" size="sm" className="gap-2"><X className="w-4 h-4" /> {t('common.cancel') || 'Annulla'}</Button>
                                        </>
                                    )}
                                </div>
                            </div>

                            {!editTranche ? (
                                <div className="space-y-2">
                                    {tranche.length === 0 && (
                                        <div className="text-sm text-[hsl(var(--muted-foreground))] p-4 rounded-lg border border-dashed border-[hsl(var(--border))] flex flex-col items-start gap-3" data-testid="sal-no-tranche-empty">
                                            <div>{t('pages.sal_no_tranche') || 'Nessuna tranche configurata'}</div>
                                            <Button onClick={manualInitTranche} size="sm" variant="outline" className="gap-2" data-testid="sal-init-tranche-button">
                                                <Plus className="w-4 h-4" /> {t('pages.sal_init_tranche_cta') || 'Inizializza tranche di default (20/30/30/20)'}
                                            </Button>
                                        </div>
                                    )}
                                    {tranche.map((tr) => {
                                        const stateColors = {
                                            pending: 'bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))]',
                                            bozza: 'bg-[hsl(45_100%_92%)] text-[hsl(35_90%_35%)]',
                                            emessa: 'bg-[hsl(210_90%_94%)] text-[hsl(210_90%_40%)]',
                                            pagata: 'bg-[hsl(145_55%_34%)] text-white',
                                            fatturata: 'bg-[hsl(220_13%_10%)] text-white',
                                        };
                                        const stateLabels = {
                                            pending: t('pages.sal_tr_pending') || 'In attesa',
                                            bozza: t('pages.sal_tr_bozza') || 'BOZZA – da inviare',
                                            emessa: t('pages.sal_tr_emessa') || 'Inviata',
                                            pagata: t('pages.sal_tr_pagata') || 'Pagata',
                                            fatturata: t('pages.sal_tr_fatturata') || 'Fatturata',
                                        };
                                        const importo = (selected.importo || 0) * (tr.importo_pct || 0) / 100;
                                        const importoMostrato = tr.importo_eur || importo;
                                        const isReached = (selected.avanzamento || 0) >= (tr.soglia_avanzamento_pct || 0);
                                        return (
                                            <div key={tr.id} className={`p-3 rounded-lg border ${tr.stato === 'bozza' ? 'border-[hsl(var(--accent))] bg-[hsl(var(--accent))]/5' : 'border-[hsl(var(--border))]'}`} data-testid={`sal-tranche-${tr.id}`}>
                                                <div className="flex flex-wrap items-center gap-2 justify-between">
                                                    <div className="flex-1 min-w-[180px]">
                                                        <div className="flex items-center gap-2 flex-wrap">
                                                            <span className="font-semibold">{tr.nome}</span>
                                                            <Badge className={`${stateColors[tr.stato] || stateColors.pending} text-xs`}>{stateLabels[tr.stato] || tr.stato}</Badge>
                                                            {tr.fatturabile && <Badge variant="outline" className="text-xs gap-1"><Receipt className="w-3 h-3" /> {t('pages.sal_tr_fatturabile') || 'Fattura finale'}</Badge>}
                                                        </div>
                                                        <div className="text-xs text-[hsl(var(--muted-foreground))] mt-1">
                                                            {t('pages.sal_tr_soglia') || 'Trigger a'} {tr.soglia_avanzamento_pct}% · {tr.importo_pct}% del totale
                                                            {isReached && tr.stato === 'pending' && <span className="ml-2 text-[hsl(var(--accent))] font-semibold">⚡ {t('pages.sal_tr_soglia_raggiunta') || 'Soglia raggiunta'}</span>}
                                                        </div>
                                                    </div>
                                                    <div className="text-right">
                                                        <div className="font-display text-lg tabular-nums">€{Number(importoMostrato).toLocaleString('it-IT', { minimumFractionDigits: 2 })}</div>
                                                    </div>
                                                </div>
                                                <div className="flex gap-2 mt-2 flex-wrap">
                                                    {tr.stato === 'bozza' && tr.proforma_id && (
                                                        <>
                                                            <Button onClick={() => openInviaDialog(tr)} size="sm" className="gap-2 bg-[hsl(var(--accent))] text-[hsl(220_13%_10%)] hover:bg-[hsl(45_100%_48%)]" data-testid={`sal-invia-${tr.id}`}>
                                                                <Send className="w-3 h-3" /> {t('pages.sal_tr_invia') || 'Invia al cliente'}
                                                            </Button>
                                                            <Button
                                                                onClick={() => {
                                                                    const tok = tr.proforma_share_token;
                                                                    if (!tok) {
                                                                        toast.error(t('pages.sal_tr_preview_unavailable') || 'Anteprima non disponibile per questa tranche. Riapri il cantiere e riprova.');
                                                                        return;
                                                                    }
                                                                    window.open(`/p/proforma/${tok}`, '_blank', 'noopener,noreferrer');
                                                                }}
                                                                size="sm" variant="outline" className="gap-2"
                                                                data-testid={`sal-preview-${tr.id}`}
                                                            >
                                                                <FileText className="w-3 h-3" /> {t('pages.sal_tr_preview') || 'Anteprima'}
                                                            </Button>
                                                        </>
                                                    )}
                                                    {tr.stato === 'emessa' && tr.proforma_id && (
                                                        <div className="w-full flex flex-col gap-2">
                                                            <div className="text-xs text-[hsl(var(--muted-foreground))] flex items-center gap-1.5">
                                                                <Send className="w-3 h-3 text-[hsl(35_90%_45%)]" />
                                                                <span>{t('pages.sal_tr_awaiting_payment') || 'Proforma inviata al cliente. Quando ricevi il pagamento, clicca il pulsante qui sotto per registrarlo.'}</span>
                                                            </div>
                                                            <div className="flex flex-wrap gap-2">
                                                                <Button
                                                                    onClick={() => { setPagRiferimento(''); setPagMetodo('bonifico'); setConfermaPagDialog(tr); }}
                                                                    size="sm" variant="outline"
                                                                    className="gap-2 border-[hsl(145_55%_34%)] text-[hsl(145_55%_28%)] hover:bg-[hsl(145_45%_94%)] font-semibold"
                                                                    data-testid={`sal-conferma-${tr.id}`}
                                                                >
                                                                    <CheckCircle2 className="w-3.5 h-3.5" /> {t('pages.sal_tr_conferma') || 'Registra incasso ricevuto'}
                                                                </Button>
                                                                {tr.proforma_share_token && (
                                                                    <Button
                                                                        onClick={() => window.open(`/p/proforma/${tr.proforma_share_token}`, '_blank', 'noopener,noreferrer')}
                                                                        size="sm" variant="ghost" className="gap-2 text-[hsl(var(--muted-foreground))]"
                                                                        data-testid={`sal-link-${tr.id}`}
                                                                    >
                                                                        <FileText className="w-3 h-3" /> {t('pages.sal_tr_open_link') || 'Apri link cliente'}
                                                                    </Button>
                                                                )}
                                                            </div>
                                                        </div>
                                                    )}
                                                    {tr.fatturabile && tr.stato === 'bozza' && (selected.avanzamento || 0) >= 99.9 && (
                                                        <Button onClick={emettiFatturaFinale} disabled={emettendoFattura} size="sm" className="gap-2 bg-[hsl(220_13%_10%)] text-white hover:bg-[hsl(220_13%_18%)]" data-testid="sal-emetti-fattura">
                                                            {emettendoFattura ? <Loader2 className="w-3 h-3 animate-spin" /> : <Receipt className="w-3 h-3" />}
                                                            {t('pages.sal_emetti_fattura_finale') || 'Emetti Fattura Finale'}
                                                        </Button>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                    <div className="mt-3 p-3 rounded-lg bg-[hsl(var(--muted))]/40 text-xs text-[hsl(var(--muted-foreground))]">
                                        <strong>{t('pages.sal_tranche_hint_title') || 'Come funziona:'}</strong> {t('pages.sal_tranche_hint') || 'Quando l\'avanzamento raggiunge la soglia di una tranche, viene creata automaticamente una proforma in BOZZA. Il titolare può rivederla e inviarla al cliente. Quando il cliente paga, la tranche si segna pagata. Al 100%, la tranche fatturabile genera la fattura finale con detrazione automatica degli anticipi.'}
                                    </div>
                                </div>
                            ) : (
                                <div className="space-y-2">
                                    <div className="grid grid-cols-12 gap-2 text-xs font-semibold text-[hsl(var(--muted-foreground))] pb-2 border-b border-[hsl(var(--border))]">
                                        <div className="col-span-4">{t('pages.sal_tr_col_nome') || 'Nome'}</div>
                                        <div className="col-span-3">{t('pages.sal_tr_col_soglia') || 'Trigger %'}</div>
                                        <div className="col-span-3">{t('pages.sal_tr_col_importo') || 'Importo %'}</div>
                                        <div className="col-span-2 text-center">{t('pages.sal_tr_col_fatt') || 'Fattura'}</div>
                                    </div>
                                    {trancheDraft.map((tr, i) => (
                                        <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`sal-tranche-edit-${i}`}>
                                            <Input value={tr.nome} onChange={(e) => updTrancheRow(i, { nome: e.target.value })} className="col-span-4 h-9" />
                                            <Input type="number" min="0" max="100" value={tr.soglia_avanzamento_pct} onChange={(e) => updTrancheRow(i, { soglia_avanzamento_pct: e.target.value })} className="col-span-3 h-9" />
                                            <Input type="number" min="0" max="100" step="0.01" value={tr.importo_pct} onChange={(e) => updTrancheRow(i, { importo_pct: e.target.value })} className="col-span-3 h-9" />
                                            <div className="col-span-1 flex justify-center">
                                                <input type="checkbox" checked={!!tr.fatturabile} onChange={(e) => updTrancheRow(i, { fatturabile: e.target.checked })} className="w-4 h-4 accent-[hsl(var(--accent))]" />
                                            </div>
                                            <Button onClick={() => delTrancheRow(i)} variant="ghost" size="sm" className="col-span-1 text-[hsl(var(--destructive))] h-9 p-1"><Trash2 className="w-4 h-4" /></Button>
                                        </div>
                                    ))}
                                    <div className="flex items-center justify-between mt-3 pt-2 border-t border-[hsl(var(--border))]">
                                        <Button onClick={addTrancheRow} size="sm" variant="outline" className="gap-1"><Plus className="w-3 h-3" /> {t('pages.sal_tr_add') || 'Aggiungi tranche'}</Button>
                                        <div className={`text-sm font-semibold ${Math.abs(sumPct(trancheDraft) - 100) < 0.5 ? 'text-[hsl(145_55%_34%)]' : 'text-[hsl(0_72%_52%)]'}`}>
                                            {t('pages.sal_tr_total') || 'Totale'}: {sumPct(trancheDraft).toFixed(1)}%
                                        </div>
                                    </div>
                                </div>
                            )}
                        </Card>
                        {analysis && (
                            <Card className="p-5 border-2 border-[hsl(var(--accent))]/30 bg-[hsl(var(--accent))]/5">
                                <div className="flex items-center gap-2 mb-3">
                                    <Sparkles className="w-5 h-5 text-[hsl(var(--accent))]" />
                                    <h3 className="font-display text-xl">{t('pages.sal_ai_title')}</h3>
                                    <Badge className={analysis.rischio_ritardo === 'alto' ? 'bg-[hsl(0_80%_95%)] text-[hsl(0_72%_52%)]' : analysis.rischio_ritardo === 'medio' ? 'bg-[hsl(45_100%_92%)] text-[hsl(220_13%_10%)]' : 'bg-[hsl(145_45%_94%)] text-[hsl(145_55%_34%)]'}>
                                        {t('pages.sal_ai_risk', { level: analysis.rischio_ritardo })}
                                    </Badge>
                                </div>
                                <p className="text-sm mb-3">{analysis.valutazione_generale}</p>
                                <div className="grid md:grid-cols-2 gap-4 text-sm">
                                    <div>
                                        <div className="font-semibold mb-1 flex items-center gap-1"><AlertTriangle className="w-4 h-4 text-[hsl(0_72%_52%)]" /> {t('pages.sal_ai_issues')}</div>
                                        <ul className="list-disc pl-4 space-y-1 text-[hsl(var(--muted-foreground))]">
                                            {(analysis.criticita || []).map((c, i) => <li key={i}>{c}</li>)}
                                        </ul>
                                    </div>
                                    <div>
                                        <div className="font-semibold mb-1">{t('pages.sal_ai_recommendations')}</div>
                                        <ul className="list-disc pl-4 space-y-1 text-[hsl(var(--muted-foreground))]">
                                            {(analysis.raccomandazioni || []).map((c, i) => <li key={i}>{c}</li>)}
                                        </ul>
                                    </div>
                                </div>
                                <div className="mt-3 text-sm" dangerouslySetInnerHTML={sanitizeStrict(t('pages.sal_ai_eta', { days: analysis.tempi_stimati_completamento_giorni }))} />
                            </Card>
                        )}
                    </div>
                ) : (
                    <Card className="p-10 text-center text-[hsl(var(--muted-foreground))]">
                        <Building2 className="w-12 h-12 mx-auto mb-3" />
                        <p>{t('pages.sal_select_or_create')}</p>
                    </Card>
                )}
            </div>

            <Dialog open={openNew} onOpenChange={setOpenNew}>
                <DialogContent>
                    <DialogHeader><DialogTitle className="font-display text-2xl">{t('pages.sal_form_title')}</DialogTitle></DialogHeader>
                    <div className="space-y-4 mt-2">
                        <div><Label>{t('pages.sal_form_name')}</Label><Input value={newCantiere.nome} onChange={(e) => setNewCantiere({...newCantiere, nome: e.target.value})} className="mt-1" data-testid="new-cantiere-name" /></div>
                        <div><Label>{t('pages.sal_form_client')}</Label><Input value={newCantiere.cliente} onChange={(e) => setNewCantiere({...newCantiere, cliente: e.target.value})} className="mt-1" /></div>
                        <div><Label>{t('pages.sal_form_address')}</Label><Input value={newCantiere.indirizzo} onChange={(e) => setNewCantiere({...newCantiere, indirizzo: e.target.value})} className="mt-1" /></div>
                        <div><Label>{t('pages.sal_form_amount')}</Label><Input type="number" value={newCantiere.importo} onChange={(e) => setNewCantiere({...newCantiere, importo: parseFloat(e.target.value) || 0})} className="mt-1" /></div>
                        <Button onClick={createCantiere} className="w-full bg-[hsl(var(--accent))] text-[hsl(220_13%_10%)] h-11" data-testid="new-cantiere-submit">{t('pages.sal_form_submit')}</Button>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Invia Proforma al cliente */}
            <Dialog open={!!inviaDialog} onOpenChange={(o) => !o && setInviaDialog(null)}>
                <DialogContent className="max-w-lg" data-testid="sal-invia-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-display text-2xl flex items-center gap-2"><Send className="w-5 h-5 text-[hsl(var(--accent))]" /> {t('pages.sal_invia_title') || 'Invia proforma al cliente'}</DialogTitle>
                    </DialogHeader>
                    {inviaDialog && (
                        <div className="space-y-4 mt-2">
                            <div className="p-3 rounded-lg bg-[hsl(var(--muted))]/40 flex items-center justify-between flex-wrap gap-2">
                                <div>
                                    <div className="text-[10px] uppercase tracking-wider text-[hsl(var(--muted-foreground))]">{t('pages.sal_invia_doc') || 'Documento'}</div>
                                    <div className="font-semibold">{inviaDialog.nome} · €{Number(inviaDialog.importo_eur || 0).toLocaleString('it-IT', { minimumFractionDigits: 2 })}</div>
                                </div>
                                {inviaDialog.proforma_share_token && (
                                    <Button size="sm" variant="outline" className="gap-2"
                                        onClick={() => window.open(`/p/proforma/${inviaDialog.proforma_share_token}`, '_blank', 'noopener,noreferrer')}>
                                        <FileText className="w-3.5 h-3.5" /> {t('pages.sal_invia_preview') || 'Anteprima'}
                                    </Button>
                                )}
                            </div>

                            <div>
                                <Label className="flex items-center gap-1.5">{t('pages.sal_invia_to') || 'Email destinatario'} <span className="text-red-500">*</span></Label>
                                <Input
                                    type="email"
                                    value={inviaEmail}
                                    onChange={(e) => setInviaEmail(e.target.value)}
                                    placeholder="cliente@esempio.it"
                                    className="mt-1"
                                    data-testid="sal-invia-email"
                                    autoFocus={!inviaEmail}
                                />
                                {!inviaEmail && (
                                    <div className="text-xs text-[hsl(35_90%_35%)] mt-1 flex items-center gap-1">
                                        <AlertTriangle className="w-3 h-3" />
                                        {t('pages.sal_invia_no_email') || 'Nessuna email salvata sul cantiere — inseriscila qui (verrà salvata per le tranche future).'}
                                    </div>
                                )}
                            </div>

                            <div>
                                <Label>{t('pages.sal_invia_subject') || 'Oggetto'}</Label>
                                <Input value={inviaSubject} onChange={(e) => setInviaSubject(e.target.value)} className="mt-1" data-testid="sal-invia-subject" />
                            </div>

                            <div>
                                <Label>{t('pages.sal_invia_message') || 'Messaggio personale (opzionale)'}</Label>
                                <Textarea
                                    value={inviaMessage}
                                    onChange={(e) => setInviaMessage(e.target.value)}
                                    placeholder={t('pages.sal_invia_message_ph') || 'Es. Ciao, come da accordi presi, ti invio la proforma per la tranche SAL 1...'}
                                    rows={3}
                                    className="mt-1"
                                    data-testid="sal-invia-msg"
                                />
                            </div>

                            <div className="flex justify-end gap-2 pt-2 border-t">
                                <Button variant="ghost" onClick={() => setInviaDialog(null)} disabled={inviaLoading}>{t('common.cancel') || 'Annulla'}</Button>
                                <Button
                                    onClick={inviaProforma}
                                    disabled={inviaLoading || !inviaEmail.trim()}
                                    className="gap-2 bg-[hsl(220_13%_10%)] hover:bg-[hsl(220_13%_18%)] text-white font-semibold"
                                    data-testid="sal-invia-confirm"
                                >
                                    {inviaLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                                    {t('pages.sal_invia_btn') || 'Invia email'}
                                </Button>
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            <RiepilogoCantiereDialog
                open={showRiepilogo}
                onOpenChange={setShowRiepilogo}
                cantiereId={selected?.id}
                cantiereName={selected?.nome}
            />

            {/* Conferma pagamento tranche */}
            <AlertDialog open={!!confermaPagDialog} onOpenChange={(o) => !o && setConfermaPagDialog(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle className="flex items-center gap-2"><CheckCircle2 className="w-5 h-5 text-[hsl(145_55%_34%)]" /> {t('pages.sal_conferma_title') || 'Conferma pagamento ricevuto'}</AlertDialogTitle>
                        <AlertDialogDescription>{t('pages.sal_conferma_desc') || 'Conferma di aver ricevuto il pagamento di questa tranche dal cliente.'}</AlertDialogDescription>
                    </AlertDialogHeader>
                    {confermaPagDialog && (
                        <div className="space-y-3 my-3">
                            <div className="rounded-lg bg-[hsl(var(--muted))]/40 p-3 text-sm">
                                <div className="flex justify-between"><span className="text-[hsl(var(--muted-foreground))]">{confermaPagDialog.nome}</span><span className="font-display text-lg tabular-nums">€{Number(confermaPagDialog.importo_eur).toLocaleString('it-IT', { minimumFractionDigits: 2 })}</span></div>
                            </div>
                            <div>
                                <Label className="text-xs">{t('pages.prev_proforma_metodo') || 'Metodo'}</Label>
                                <Select value={pagMetodo} onValueChange={setPagMetodo}>
                                    <SelectTrigger className="mt-1 h-11"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="bonifico">Bonifico</SelectItem>
                                        <SelectItem value="contanti">Contanti</SelectItem>
                                        <SelectItem value="assegno">Assegno</SelectItem>
                                        <SelectItem value="carta">Carta</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label className="text-xs">{t('pages.prev_proforma_riferimento') || 'Riferimento'}</Label>
                                <Input value={pagRiferimento} onChange={(e) => setPagRiferimento(e.target.value)} className="mt-1 h-11" placeholder="CRO 123456789" />
                            </div>
                        </div>
                    )}
                    <AlertDialogFooter>
                        <AlertDialogCancel disabled={pagLoading}>{t('common.cancel') || 'Annulla'}</AlertDialogCancel>
                        <AlertDialogAction onClick={(e) => { e.preventDefault(); confermaPagamentoTranche(); }} disabled={pagLoading} className="bg-[hsl(145_55%_34%)] hover:bg-[hsl(145_55%_28%)] text-white">
                            {pagLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
                            {t('pages.prev_proforma_conferma_btn') || 'Conferma'}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Risultato fattura finale */}
            <AlertDialog open={!!fatturaResult} onOpenChange={(o) => !o && setFatturaResult(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle className="flex items-center gap-2"><Receipt className="w-5 h-5 text-[hsl(var(--accent))]" /> {t('pages.sal_fattura_emessa') || 'Fattura finale emessa!'}</AlertDialogTitle>
                    </AlertDialogHeader>
                    {fatturaResult && (
                        <div className="space-y-3 my-3">
                            <div className="rounded-lg bg-[hsl(var(--muted))]/40 p-3 space-y-1 text-sm">
                                <div className="flex justify-between"><span>{t('pages.fat_form_imponibile')}</span><span className="tabular-nums">€{Number(fatturaResult.fattura.imponibile).toLocaleString('it-IT', { minimumFractionDigits: 2 })}</span></div>
                                <div className="flex justify-between"><span>{t('pages.prev_form_iva')}</span><span className="tabular-nums">€{Number(fatturaResult.fattura.iva_totale).toLocaleString('it-IT', { minimumFractionDigits: 2 })}</span></div>
                                <div className="flex justify-between font-display text-lg border-t pt-1 mt-1"><span>{t('pages.fat_sheet_total_due')}</span><span className="tabular-nums">€{Number(fatturaResult.fattura.totale).toLocaleString('it-IT', { minimumFractionDigits: 2 })}</span></div>
                            </div>
                            <div className="rounded-lg bg-[hsl(145_45%_94%)] p-3 text-sm">
                                <div className="flex justify-between"><span>{t('pages.sal_anticipi_detratti') || 'Anticipi/SAL detratti'}</span><span className="tabular-nums text-[hsl(145_55%_34%)]">-€{Number(fatturaResult.anticipi_detratti).toLocaleString('it-IT', { minimumFractionDigits: 2 })}</span></div>
                                <div className="flex justify-between font-display border-t border-[hsl(145_55%_34%)]/30 pt-1 mt-1"><span>{t('pages.sal_saldo_da_pagare') || 'Saldo da pagare'}</span><span className="tabular-nums">€{Number(fatturaResult.saldo_da_pagare).toLocaleString('it-IT', { minimumFractionDigits: 2 })}</span></div>
                            </div>
                            {fatturaResult.saldo_proforma_id && (
                                <div className="text-xs text-[hsl(var(--muted-foreground))]">{t('pages.sal_saldo_proforma_info') || 'È stata creata una proforma del saldo da inviare al cliente.'}</div>
                            )}
                        </div>
                    )}
                    <AlertDialogFooter>
                        <AlertDialogCancel>{t('common.close') || 'Chiudi'}</AlertDialogCancel>
                        <AlertDialogAction onClick={() => { setFatturaResult(null); navigate('/app/fatture'); }} className="bg-[hsl(var(--accent))] text-[hsl(220_13%_10%)] hover:bg-[hsl(45_100%_48%)]">
                            <Receipt className="w-4 h-4 mr-2" /> {t('pages.sal_vai_fatture') || 'Vai a Fatture'}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
